/*
  What an engineer may do to one line, worked out from the line and its element (CP-12). The server decides again on every
  command (commands/lines.py); this only hides the buttons that would be refused, so a person is not offered a step that
  cannot go through. The rules are the server's:

  - Take out (`withdraw_line`): a line that was never validated (it waits, or came back). Its maker takes it out; the
    line does not say who made it, so the server's refusal is shown when it is someone else's.
  - Mark for removal (`mark_line_removal`): a validated line this discipline made, with no change or removal waiting.
  - Take the mark off (`lift_line_removal`): only the engineer who marked it.
  - Drop my change (`drop_line_next_version`): only the engineer who made the Next version.
  - A line the app made follows its element and has no buttons; a loaded line stays (it is not marked for removal).
*/

import type { ElementRow, LineRow } from "../../../api/types";

const NEVER_VALIDATED: ReadonlySet<string> = new Set(["accepted", "added_by_hand", "sent_back", "rejected"]);
const GONE: ReadonlySet<string> = new Set(["withdrawn", "removed"]);

export interface LineChoices {
  /** "Take out": the line was never validated. */
  takeOut: boolean;
  /** "Mark for removal": the line is validated and nothing waits on it. */
  markRemoval: boolean;
  /** "Take the mark off": the person marked it. */
  liftRemoval: boolean;
  /** "Drop my change": the person made the Next version of the line. */
  dropMine: boolean;
  /** Someone else's Next version waits on the line: who, or "" when the view does not say. */
  othersChange: string | null;
  /** Someone else marked the line for removal. */
  othersRemoval: boolean;
}

export const NO_CHOICES: LineChoices = { takeOut: false, markRemoval: false, liftRemoval: false, dropMine: false, othersChange: null, othersRemoval: false };

/** A line that went through the SME, or is loaded, or follows a validated element (a symbol, a derived line). */
export function lineIsValidatedLike(line: Pick<LineRow, "state" | "origin">, element: Pick<ElementRow, "state" | "origin">): boolean {
  if (line.state === "validated") return true;
  if (line.state == null) {
    if (line.origin === "loaded") return true;
    return element.state === "validated" || (element.state == null && element.origin === "loaded");
  }
  return false;
}

export function lineIsGone(line: Pick<LineRow, "state">): boolean {
  return !!line.state && GONE.has(line.state);
}

/** The line was never validated: it waits for the SME or came back, so it can be taken out. */
export function lineNeverValidated(line: Pick<LineRow, "state">): boolean {
  return !!line.state && NEVER_VALIDATED.has(line.state);
}

/** The buttons a line offers to the person with this membership. */
export function lineChoices(line: LineRow, element: Pick<ElementRow, "state" | "origin">, membershipId: string | null | undefined): LineChoices {
  if (line.origin === "app" || lineIsGone(line)) return NO_CHOICES;
  if (element.state === "withdrawn" || element.state === "removed") return NO_CHOICES;
  const mine = (maker: string | null | undefined) => !!membershipId && !!maker && maker === membershipId;
  const pendingByOther = !!line.pending && !mine(line.pending.made_by);
  const removalByOther = !!line.removal && !mine(line.removal.marked_by);
  return {
    takeOut: lineNeverValidated(line),
    markRemoval: line.origin === "work" && lineIsValidatedLike(line, element) && !line.removal && !line.pending,
    liftRemoval: !!line.removal && mine(line.removal.marked_by),
    dropMine: !!line.pending && mine(line.pending.made_by),
    othersChange: pendingByOther ? (line.pending_by?.display ?? "") : null,
    othersRemoval: removalByOther,
  };
}

/** Whether any button or note shows for this line. */
export function hasChoices(choices: LineChoices): boolean {
  return choices.takeOut || choices.markRemoval || choices.liftRemoval || choices.dropMine || choices.othersChange !== null || choices.othersRemoval;
}
